import OwlCarousel from 'react-owl-carousel';
import 'owl.carousel/dist/assets/owl.carousel.css'
import 'owl.carousel/dist/assets/owl.theme.default.css'
import SinglePost from './SinglePost'
import LightboxModal from './Elements/Modal';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { __, sprintf } from '@wordpress/i18n';
import CarouselAutoplayControl from './Elements/CarouselAutoplayControl';

const CarouselLayout = ({ feedSettings, headerData, posts, isPro, editorActiveDevice, isFeedEditor }) => {

    const [activePost, setActivePost] = useState(null);
    const [activeIndex, setActiveIndex] = useState(null);
    const [isLightboxVisible, setIsLightboxVisible] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    // SMASH-1377 a11y (F1): react-owl-carousel renders its prev/next arrows and
    // pagination dots as bare <button>s with no accessible name (4.1.2 / 2.4.4).
    // The library exposes no per-control labeling prop for the dots, so we label
    // them post-render via a ref + MutationObserver (owl re-renders the dots on
    // resize / navigation, so a one-shot pass is not enough).
    const carouselRef = useRef(null);

    // SMASH-1377 v3-r11: restore focus to the triggering post button when the
    // conditionally-mounted lightbox closes (no Radix DialogTrigger to restore
    // to, so focus would otherwise drop to <body> — WCAG 2.4.3).
    const triggerRef = useRef(null);

    useEffect(() => {
        if (!isLightboxVisible && triggerRef.current) {
            triggerRef.current.focus();
            triggerRef.current = null;
        }
    }, [isLightboxVisible]);

    // SMASH-1382 v3-frontend (T5, WCAG 2.2.2): the autoplay pause control lives
    // in its own sibling component so toggling it never re-renders this layout
    // — react-owl-carousel destroys+recreates owl on every parent update (which
    // would restart autoplay and reset the slide). See CarouselAutoplayControl.
    const autoplayEnabled = !!feedSettings.carouselEnableAutoplay;

    const labelOwlControls = useCallback(() => {
        const root = carouselRef.current;
        if (!root) return;

        // SMASH-1377 v3-r13: react-owl-carousel renders prev/next/dots as
        // <button role="presentation">. Once we give them an accessible name
        // (above) they become labeled interactive controls, which conflicts
        // with role=presentation (axe: aria-allowed-role,
        // presentation-role-conflict). Strip the role so the native <button>
        // role is restored. Idempotent + inside the observer so it survives
        // owl re-renders (resize / navigation).
        const prev = root.querySelector('.owl-nav .owl-prev');
        if (prev && !prev.getAttribute('aria-label')) {
            prev.setAttribute('aria-label', __('Previous', 'sb-customizer'));
        }
        if (prev && prev.getAttribute('role') === 'presentation') {
            prev.removeAttribute('role');
        }
        const next = root.querySelector('.owl-nav .owl-next');
        if (next && !next.getAttribute('aria-label')) {
            next.setAttribute('aria-label', __('Next', 'sb-customizer'));
        }
        if (next && next.getAttribute('role') === 'presentation') {
            next.removeAttribute('role');
        }
        root.querySelectorAll('.owl-dots .owl-dot').forEach((dot, i) => {
            const label = sprintf(
                /* translators: %d: slide number */
                __('Go to slide %d', 'sb-customizer'),
                i + 1
            );
            if (dot.getAttribute('aria-label') !== label) {
                dot.setAttribute('aria-label', label);
            }
            if (dot.getAttribute('role') === 'presentation') {
                dot.removeAttribute('role');
            }
        });

        // SMASH-1382 v3-frontend (T6): in loop mode owl prepends/appends
        // duplicate ".cloned" slides. Their post <button>s land in the tab
        // order and screen readers announce every post twice. Hide the clones
        // from AT and take their focusables out of the tab order. Idempotent +
        // inside the observer so it survives owl re-renders.
        root.querySelectorAll('.owl-item.cloned').forEach((clone) => {
            if (clone.getAttribute('aria-hidden') !== 'true') {
                clone.setAttribute('aria-hidden', 'true');
            }
            clone.querySelectorAll('button, a[href], input, select, textarea, [tabindex]').forEach((el) => {
                if (el.getAttribute('tabindex') !== '-1') {
                    el.setAttribute('tabindex', '-1');
                }
            });
        });
    }, []);

    useEffect(() => {
        const root = carouselRef.current;
        if (!root) return;

        labelOwlControls();

        const observer = new MutationObserver(() => labelOwlControls());
        observer.observe(root, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [labelOwlControls, posts]);

    let desktopNumber = feedSettings.carouselDesktopColumns;

    if (isFeedEditor) {
        switch (editorActiveDevice.device) {
            case 'mobile':
                desktopNumber = feedSettings.carouselMobileColumns;
                break;
            case 'tablet':
                desktopNumber = feedSettings.carouselTabletColumns;
                break;
            default:
                desktopNumber = feedSettings.carouselDesktopColumns;
                break;
        }
    }

    let responsiveLayoutData = {
        0: {
            items: feedSettings.carouselMobileColumns,
            rows: feedSettings.carouselMobileRows
        },
        600: {
            items: feedSettings.carouselTabletColumns,
            rows: feedSettings.carouselTabletRows
        },
        1024: {
            items: desktopNumber,
            rows: feedSettings.carouselDesktopRows
        }
    };

    // SMASH-1853: Swipe View is deliberately NOT wired here (unlike
    // GridLayout/ListLayout/MasonryLayout). The PR that shipped Swipe View
    // originally cited the spec's "Carousel Reels stay in the classic
    // lightbox" non-goal as the reason — that's wrong and was corrected on
    // review (PR #191): that non-goal is about a Reel embedded inside a
    // carousel POST, not this carousel LAYOUT, and this layout's click path
    // below is otherwise identical to the three that ARE wired. The real
    // reason is react-owl-carousel: it owns horizontal drag on every slide,
    // and opening a vertical swipe viewer out of a horizontal-drag surface
    // needs an on-device gesture-conflict check before it's safe to wire up
    // — tracked on SMASH-1853, not asserted here as already resolved.
    const handleModalOpen = (postIndex, triggerEl) => {
        triggerRef.current = triggerEl || null;
        setActivePost(posts[postIndex]);
        setActiveIndex(postIndex);
        setIsLightboxVisible(true);
    }

    //
    const handleCloseModal = () => {
        setActivePost(null);
        setIsLightboxVisible(false);
        document.body.classList.remove('sbt-lightbox-modal-open');
    }

    //
    const handlePreviousItem = () => {
        const currentPostIndex = posts.findIndex(post => post.id === activePost.id);
        if (currentPostIndex === 0) {
            //
        }
        else {
            setActivePost(posts[currentPostIndex - 1]);
            setActiveIndex(currentPostIndex - 1);
        }
    }

    //
    const handleNextItem = () => {
        const currentPostIndex = posts.findIndex(post => post.id === activePost.id);
        if (currentPostIndex === posts.length - 1) {
            //
        }
        else {
            setActivePost(posts[currentPostIndex + 1]);
            setActiveIndex(currentPostIndex + 1);
        }
    }

    return (
        <>
            <div ref={carouselRef} className="sbt-carousel-ctn sb-fs" style={{ position: 'relative' }}>
            {autoplayEnabled && <CarouselAutoplayControl carouselRef={carouselRef} />}
            <OwlCarousel
                responsive={responsiveLayoutData}
                margin={parseInt(feedSettings.horizontalSpacing)}
                loop={feedSettings.carouselLoopType === 'infinity'}
                rewind={feedSettings.carouselLoopType === 'rewind'}
                nav={feedSettings.carouselShowArrows}
                dots={feedSettings.carouselShowPagination}
                autoplay={feedSettings.carouselEnableAutoplay}
                autoplayTimeout={feedSettings.carouselIntervalTime}
            >
                {
                    posts.map((post, postIndex) =>
                        <SinglePost
                            headerData={headerData}
                            post={post}
                            postIndex={postIndex}
                            feedSettings={feedSettings}
                            key={postIndex}
                            isPro={isPro}
                            isFeedEditor={isFeedEditor}
                            handlePostClick={handleModalOpen}
                            isPlaying={isPlaying}
                            setIsPlaying={setIsPlaying}
                            activeIndex={activeIndex}
                            setActiveIndex={setActiveIndex}
                        />
                    )
                }
            </OwlCarousel>
            </div>

            {
                isLightboxVisible && activePost &&
                createPortal(
                    <LightboxModal closeModal={handleCloseModal}
                        post={activePost}
                        headerData={headerData}
                        feedSettings={feedSettings}
                        handleNext={handleNextItem}
                        handlePrev={handlePreviousItem}
                        showNext={
                            !(activeIndex === posts.length - 1)
                        }
                        showPrev={
                            !(activeIndex === 0)
                        }
                        isPro={isPro}
                        isLightboxVisible={isLightboxVisible}
                        setIsLightboxVisible={setIsLightboxVisible}
                    />,
                    document.body
                )
            }
        </>
    )
}

export default CarouselLayout;